import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Alert,
  Badge,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import {
  ConfigSchema,
  claimGoogleReserveIssuedCode,
  getGoogleReserveStatus,
  install,
  IntegrationProvider,
  isCredentialsConfigured,
  requestGoogleReserveUnitCode,
  TenantIntegration,
  uninstall,
  update,
} from "../../services/integrationService";
import { Unit } from "../../services/unitService";
import {
  getHelpArticleUrl,
  HELP_ARTICLE_GOOGLE_BOOKINGS,
  HELP_ARTICLE_META_COMMERCE,
} from "../../helpers/helpCenter";
import { extractGoogleReserveUnitCode } from "../../helpers/googleReserveCode";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import GoogleReservePanel from "./GoogleReservePanel";
import MetaCommercePanel from "./MetaCommercePanel";

const RECOMMENDED_PROVIDER_SLUG = "iugis";
const GOOGLE_RESERVE_SLUG = "google-reserve";
const META_COMMERCE_SLUG = "meta-commerce";
const PAYMENTS_CAPABILITY = "payments";
const PAYMENTS_TERMINAL_CAPABILITY = "payments.terminal";
const BOOKING_GOOGLE_CAPABILITY = "booking.google";

const requiresUnit = (capability: string): boolean =>
  capability === PAYMENTS_CAPABILITY
  || capability === PAYMENTS_TERMINAL_CAPABILITY
  || capability === BOOKING_GOOGLE_CAPABILITY;

export interface IntegrationConfigModalProps {
  isOpen: boolean;
  provider: IntegrationProvider | null;
  installedIntegration: TenantIntegration | null;
  units: Unit[];
  onClose: () => void;
  onSaved: () => void;
  onUninstalled: () => void;
  /** Prefill unit from deep link (?unit_id=) */
  deepLinkUnitId?: number | null;
  /** Auto-claim issued code after open (?claim=1) */
  autoClaim?: boolean;
}

interface IntegrationFormValues {
  capability: string;
  unit_id: string;
  enabled: boolean;
  credentials: Record<string, string>;
  template_created: string;
  template_reminder: string;
}

const buildCredentialFields = (
  schema: ConfigSchema | null | undefined
): string[] => {
  if (!schema?.properties) {
    return [];
  }

  return Object.keys(schema.properties);
};

const emptyCredentials = (fields: string[]): Record<string, string> =>
  fields.reduce<Record<string, string>>((acc, field) => {
    acc[field] = "";
    return acc;
  }, {});

const IntegrationConfigModal: React.FC<IntegrationConfigModalProps> = ({
  isOpen,
  provider,
  installedIntegration,
  units,
  onClose,
  onSaved,
  onUninstalled,
  deepLinkUnitId = null,
  autoClaim = false,
}) => {
  const { t, i18n } = useTranslation();

  const isEditMode = installedIntegration !== null;
  const credentialsConfigured = isCredentialsConfigured(installedIntegration);
  const isGoogleReserve = provider?.slug === GOOGLE_RESERVE_SLUG;
  const isMetaCommerce = provider?.slug === META_COMMERCE_SLUG;
  const [wizardStep, setWizardStep] = useState(1);
  const [requestingCode, setRequestingCode] = useState(false);
  const [claimingCode, setClaimingCode] = useState(false);
  const [issuedCodeAvailable, setIssuedCodeAvailable] = useState(false);
  const [autoClaimDone, setAutoClaimDone] = useState(false);

  const credentialFields = useMemo(
    () => buildCredentialFields(provider?.config_schema),
    [provider?.config_schema]
  );

  const requiredCredentialFields = useMemo(
    () => provider?.config_schema?.required ?? [],
    [provider?.config_schema]
  );

  const validationSchema = useMemo(() => {
    const credentialRules = credentialFields.reduce<
      Record<string, Yup.StringSchema>
    >((acc, field) => {
      const isRequired =
        !isEditMode ||
        !credentialsConfigured ||
        requiredCredentialFields.includes(field);

      acc[field] = isRequired
        ? Yup.string().trim().required(
            t("studio.integrations.validation.fieldRequired", { field })
          )
        : Yup.string().trim();

      return acc;
    }, {});

    return Yup.object({
      capability: Yup.string().required(
        t("studio.integrations.validation.capabilityRequired")
      ),
      unit_id: Yup.string().when("capability", {
        is: (capability: string) => requiresUnit(capability),
        then: (schema) =>
          schema.required(t("studio.integrations.validation.unitRequired")),
        otherwise: (schema) => schema.notRequired(),
      }),
      enabled: Yup.boolean(),
      credentials: Yup.object(credentialRules),
    });
  }, [
    credentialFields,
    credentialsConfigured,
    isEditMode,
    requiredCredentialFields,
    t,
  ]);

  const formik = useFormik<IntegrationFormValues>({
    enableReinitialize: true,
    initialValues: {
      capability:
        installedIntegration?.capability ??
        provider?.capabilities[0] ??
        "",
      unit_id: installedIntegration?.unit_id
        ? String(installedIntegration.unit_id)
        : deepLinkUnitId
          ? String(deepLinkUnitId)
          : "",
      enabled: installedIntegration?.enabled ?? true,
      credentials: emptyCredentials(credentialFields),
      template_created: String(
        (installedIntegration?.settings?.templates as { created?: string } | undefined)
          ?.created ?? ""
      ),
      template_reminder: String(
        (installedIntegration?.settings?.templates as { reminder?: string } | undefined)
          ?.reminder ?? ""
      ),
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      if (!provider) {
        return;
      }

      helpers.setSubmitting(true);

      try {
        const unitId =
          requiresUnit(values.capability) && values.unit_id
            ? Number(values.unit_id)
            : null;

        const filledCredentials = Object.fromEntries(
          Object.entries(values.credentials).filter(
            ([, value]) => value.trim() !== ""
          )
        );

        const iugisSettings =
          provider.slug === RECOMMENDED_PROVIDER_SLUG
            ? {
                templates: {
                  created: values.template_created.trim(),
                  reminder: values.template_reminder.trim(),
                },
              }
            : undefined;

        if (isEditMode && installedIntegration) {
          const payload: Parameters<typeof update>[0] = {
            provider_slug: provider.slug,
            capability: values.capability,
            unit_id: unitId,
            enabled: values.enabled,
          };

          if (Object.keys(filledCredentials).length > 0) {
            for (const field of requiredCredentialFields) {
              if (!values.credentials[field]?.trim()) {
                helpers.setFieldError(
                  `credentials.${field}`,
                  t("studio.integrations.validation.fieldRequired", { field })
                );
                helpers.setSubmitting(false);
                return;
              }
            }

            payload.credentials = values.credentials;
          }

          if (iugisSettings) {
            payload.settings = {
              ...(installedIntegration.settings ?? {}),
              ...iugisSettings,
            };
          }

          await update(payload);
        } else {
          await install({
            provider_slug: provider.slug,
            capability: values.capability,
            unit_id: unitId,
            credentials: values.credentials,
            settings: iugisSettings,
            enabled: values.enabled,
          });
        }

        onSaved();
        onClose();
      } catch {
        // Error message surfaced by api_helper interceptor / toast in parent
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      setWizardStep(1);
      setIssuedCodeAvailable(false);
      setAutoClaimDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isGoogleReserve || !autoClaim || autoClaimDone) {
      return;
    }

    const unitId = formik.values.unit_id
      ? Number(formik.values.unit_id)
      : deepLinkUnitId ?? installedIntegration?.unit_id ?? null;

    if (!unitId) {
      return;
    }

    let cancelled = false;

    const run = async (): Promise<void> => {
      setAutoClaimDone(true);
      if (!formik.values.unit_id) {
        await formik.setFieldValue("unit_id", String(unitId));
      }
      setWizardStep(2);
      setClaimingCode(true);
      try {
        const response = await claimGoogleReserveIssuedCode(unitId);
        if (cancelled) {
          return;
        }
        await formik.setFieldValue("credentials.merchant_id", response.data.unit_code);
        toast.success(t("studio.integrations.googleReserve.claimIssuedSuccess"));
        setIssuedCodeAvailable(false);
      } catch {
        if (!cancelled) {
          toast.error(t("studio.integrations.googleReserve.claimIssuedError"));
        }
      } finally {
        if (!cancelled) {
          setClaimingCode(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isGoogleReserve,
    autoClaim,
    autoClaimDone,
    deepLinkUnitId,
    installedIntegration?.unit_id,
    formik.values.unit_id,
    t,
  ]);

  useEffect(() => {
    if (!isOpen || !isGoogleReserve) {
      return;
    }

    const unitId = installedIntegration?.unit_id
      ?? (formik.values.unit_id ? Number(formik.values.unit_id) : null);

    if (!unitId) {
      setIssuedCodeAvailable(false);
      return;
    }

    void getGoogleReserveStatus(unitId)
      .then((response) => {
        setIssuedCodeAvailable(response.data.issued_code?.available === true);
      })
      .catch(() => {
        setIssuedCodeAvailable(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isGoogleReserve, installedIntegration?.unit_id, formik.values.unit_id, wizardStep]);

  const fieldLabel = (field: string): string => {
    const key = `studio.integrations.fields.${field}.label`;
    const translated = t(key);

    if (translated !== key) {
      return translated;
    }

    return t("studio.integrations.fields.credential.label", { field });
  };

  const fieldPlaceholder = (field: string): string => {
    const key = `studio.integrations.fields.${field}.placeholder`;
    const translated = t(key);

    if (translated !== key) {
      return translated;
    }

    return t("studio.integrations.fields.credential.placeholder", { field });
  };

  const configuredHint = (field: string): string => {
    const key = `studio.integrations.fields.${field}.configuredHint`;
    const translated = t(key);

    if (translated !== key) {
      return translated;
    }

    return t("studio.integrations.fields.credential.configuredHint", { field });
  };

  const capabilityLabel = (capability: string): string => {
    const key = `studio.integrations.capabilities.${capability}`;
    const translated = t(key);

    return translated !== key ? translated : capability;
  };

  const handleRequestUnitCode = async (): Promise<void> => {
    const confirmation = await Swal.fire({
      title: t("studio.integrations.googleReserve.codeRequestConfirmTitle"),
      text: t("studio.integrations.googleReserve.codeRequestConfirmBody"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("studio.integrations.googleReserve.codeRequestConfirmYes"),
      cancelButtonText: t("studio.integrations.googleReserve.codeRequestConfirmNo"),
      confirmButtonColor: "#0ab39c",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setRequestingCode(true);
    try {
      const unitId = formik.values.unit_id
        ? Number(formik.values.unit_id)
        : null;
      const response = await requestGoogleReserveUnitCode(unitId);
      const ticketNumber = response.data?.ticket_public_number;
      toast.success(
        ticketNumber
          ? t("studio.integrations.googleReserve.codeRequestSuccessWithTicket", {
              number: ticketNumber,
            })
          : t("studio.integrations.googleReserve.codeRequestSuccess")
      );
    } catch {
      toast.error(t("studio.integrations.googleReserve.codeRequestError"));
    } finally {
      setRequestingCode(false);
    }
  };

  const handleClaimIssuedCode = async (): Promise<void> => {
    const unitId = formik.values.unit_id
      ? Number(formik.values.unit_id)
      : installedIntegration?.unit_id;

    if (!unitId) {
      toast.error(t("studio.integrations.validation.unitRequired"));
      return;
    }

    setClaimingCode(true);
    try {
      const response = await claimGoogleReserveIssuedCode(unitId);
      await formik.setFieldValue("credentials.merchant_id", response.data.unit_code);
      toast.success(t("studio.integrations.googleReserve.claimIssuedSuccess"));
      setIssuedCodeAvailable(false);
    } catch {
      toast.error(t("studio.integrations.googleReserve.claimIssuedError"));
    } finally {
      setClaimingCode(false);
    }
  };

  const handleMerchantPaste = (
    event: React.ClipboardEvent<HTMLInputElement>
  ): void => {
    const pasted = event.clipboardData.getData("text");
    const extracted = extractGoogleReserveUnitCode(pasted);

    if (!extracted || extracted === pasted.trim()) {
      return;
    }

    event.preventDefault();
    void formik.setFieldValue("credentials.merchant_id", extracted);
    toast.info(t("studio.integrations.googleReserve.pasteExtracted"));
  };

  const maskedSavedCode = useMemo((): string | null => {
    if (!isGoogleReserve || !installedIntegration) {
      return null;
    }

    const settings = installedIntegration.settings ?? {};
    const masked = settings.merchant_id_masked ?? settings.merchant_id;

    return typeof masked === "string" && masked !== "" ? masked : null;
  }, [installedIntegration, isGoogleReserve]);

  if (!provider) {
    return null;
  }

  const modalTitle = isEditMode
    ? t("studio.integrations.modal.configureTitle", { name: provider.name })
    : t("studio.integrations.modal.connectTitle", { name: provider.name });

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>{modalTitle}</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          {provider.slug === RECOMMENDED_PROVIDER_SLUG && (
            <Alert color="primary" className="border-0 bg-primary-subtle text-primary">
              {t("studio.integrations.badge.recommendedHint")}
            </Alert>
          )}

          {provider.slug === RECOMMENDED_PROVIDER_SLUG && (
            <Alert color="info" className="border-0">
              <strong className="d-block mb-2">
                {t("studio.integrations.iugis.tokenStepsTitle")}
              </strong>
              <ol className="mb-2 ps-3">
                <li>
                  {t("studio.integrations.iugis.tokenStep1")}{" "}
                  <a
                    href="https://chat.iugis.com.br/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    chat.iugis.com.br
                  </a>
                </li>
                <li>{t("studio.integrations.iugis.tokenStep2")}</li>
                <li>{t("studio.integrations.iugis.tokenStep3")}</li>
                <li>{t("studio.integrations.iugis.tokenStep4")}</li>
                <li>{t("studio.integrations.iugis.tokenStep5")}</li>
                <li>{t("studio.integrations.iugis.tokenStep6")}</li>
              </ol>
              <p className="mb-0 small">
                {t("studio.integrations.iugis.templatesHelpIntro")}{" "}
                <a href="https://ajuda.adatis.com.br/configuracoes/iugis-numero-proprio.html" target="_blank" rel="noreferrer">
                  {t("studio.integrations.iugis.templatesHelpLink")}
                </a>
              </p>
            </Alert>
          )}

          {isGoogleReserve && !isEditMode && (
            <>
              <Alert color="info" className="border-0">
                <p className="mb-2">{t("studio.integrations.googleReserve.wizardIntro")}</p>
                <p className="mb-0 small">
                  {t("studio.integrations.googleReserve.helpIntro")}{" "}
                  <a
                    href={getHelpArticleUrl(HELP_ARTICLE_GOOGLE_BOOKINGS, i18n.language)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("studio.integrations.googleReserve.helpLink")}
                  </a>
                </p>
              </Alert>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {[1, 2, 3].map((step) => (
                  <Badge
                    key={step}
                    color={wizardStep === step ? "primary" : "light"}
                    className={wizardStep === step ? "" : "text-muted"}
                  >
                    {t(`studio.integrations.googleReserve.wizardStep${step}`)}
                  </Badge>
                ))}
              </div>
            </>
          )}

          {isMetaCommerce && !isEditMode && (
            <>
              <Alert color="info" className="border-0">
                <p className="mb-2">{t("studio.integrations.metaCommerce.wizardIntro")}</p>
                <ol className="small mb-2 ps-3">
                  <li>{t("studio.integrations.metaCommerce.wizardStep1Text")}</li>
                  <li>{t("studio.integrations.metaCommerce.wizardStep2Text")}</li>
                  <li>{t("studio.integrations.metaCommerce.wizardStep3Text")}</li>
                </ol>
                <p className="mb-0 small">
                  <a
                    href={getHelpArticleUrl(HELP_ARTICLE_META_COMMERCE, i18n.language)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("studio.integrations.metaCommerce.helpLink")}
                  </a>
                </p>
              </Alert>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {[1, 2, 3].map((step) => (
                  <Badge
                    key={step}
                    color={wizardStep === step ? "primary" : "light"}
                    className={wizardStep === step ? "" : "text-muted"}
                  >
                    {t(`studio.integrations.metaCommerce.wizardStep${step}`)}
                  </Badge>
                ))}
              </div>
              {wizardStep === 1 && (
                <Alert color="warning" className="border-0">
                  {t("studio.integrations.metaCommerce.wizardPrereq")}
                </Alert>
              )}
            </>
          )}

          {isGoogleReserve && isEditMode && (
            <Alert color="info" className="border-0 py-2">
              <p className="mb-2">{t("studio.integrations.googleReserve.configureIntro")}</p>
              <p className="mb-0 small">
                {t("studio.integrations.googleReserve.helpIntro")}{" "}
                <a
                  href={getHelpArticleUrl(HELP_ARTICLE_GOOGLE_BOOKINGS, i18n.language)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("studio.integrations.googleReserve.helpLink")}
                </a>
              </p>
            </Alert>
          )}

          {(isEditMode || provider.capabilities.length === 1) && (
            <div className="mb-3">
              <Label>{t("studio.integrations.fields.capability.label")}</Label>
              <div className="text-muted">
                {capabilityLabel(formik.values.capability)}
              </div>
            </div>
          )}

          {provider.capabilities.length > 1 && !isEditMode && (
            <div className="mb-3">
              <Label for="integration-capability">
                {t("studio.integrations.fields.capability.label")}
              </Label>
              <Input
                id="integration-capability"
                name="capability"
                type="select"
                value={formik.values.capability}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.capability && Boolean(formik.errors.capability)
                }
                disabled={isEditMode}
              >
                <option value="">
                  {t("studio.integrations.fields.capability.placeholder")}
                </option>
                {provider.capabilities.map((capability: string) => (
                  <option key={capability} value={capability}>
                    {capabilityLabel(capability)}
                  </option>
                ))}
              </Input>
              {formik.touched.capability && formik.errors.capability && (
                <FormFeedback>{formik.errors.capability}</FormFeedback>
              )}
            </div>
          )}

          {requiresUnit(formik.values.capability) &&
            (!isGoogleReserve || isEditMode || wizardStep === 1 || wizardStep === 3) && (
            <div className="mb-3">
              <Label for="integration-unit">
                {t("studio.integrations.fields.unit.label")}
              </Label>
              <Input
                id="integration-unit"
                name="unit_id"
                type="select"
                value={formik.values.unit_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.unit_id && Boolean(formik.errors.unit_id)
                }
                disabled={isEditMode || (isGoogleReserve && !isEditMode && wizardStep === 3)}
              >
                <option value="">
                  {t("studio.integrations.fields.unit.placeholder")}
                </option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
              {formik.touched.unit_id && formik.errors.unit_id && (
                <FormFeedback>{formik.errors.unit_id}</FormFeedback>
              )}
              {formik.values.capability === BOOKING_GOOGLE_CAPABILITY && (
                <div className="form-text">
                  {t("studio.integrations.googleReserve.unitHelp")}
                </div>
              )}
            </div>
          )}

          {isEditMode && (
            <div className="mb-4">
              <div className="form-check form-switch form-switch-md">
                <Input
                  id="integration-enabled"
                  name="enabled"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={formik.values.enabled}
                  onChange={formik.handleChange}
                />
                <Label className="form-check-label" for="integration-enabled">
                  {t("studio.integrations.fields.enabled.label")}
                </Label>
              </div>
              <small className="text-muted">
                {t("studio.integrations.fields.enabled.hint")}
              </small>
            </div>
          )}

          {credentialFields.length > 0 &&
            ((!isGoogleReserve && !isMetaCommerce) ||
              isEditMode ||
              wizardStep >= 2) && (
            <>
              <hr className="my-3" />
              <h6 className="mb-3">{t("studio.integrations.modal.credentialsSection")}</h6>
              {isMetaCommerce && !isEditMode && wizardStep === 2 && (
                <p className="text-muted small mb-3">
                  {t("studio.integrations.metaCommerce.credentialsHelp")}
                </p>
              )}
              {isGoogleReserve &&
                !isEditMode &&
                wizardStep === 2 &&
                !(formik.values.credentials.merchant_id ?? "").trim() && (
                <div className="border rounded p-3 mb-3 text-center bg-light">
                  <div className="fw-semibold mb-1">
                    {t("studio.integrations.googleReserve.codeEmptyTitle")}
                  </div>
                  <p className="text-muted small mb-3">
                    {t("studio.integrations.googleReserve.codeEmptyBody")}
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <Button
                      color="soft-primary"
                      size="sm"
                      type="button"
                      disabled={requestingCode || !formik.values.unit_id}
                      onClick={() => void handleRequestUnitCode()}
                    >
                      {requestingCode ? (
                        <Spinner size="sm" />
                      ) : (
                        t("studio.integrations.googleReserve.codeRequestCta")
                      )}
                    </Button>
                    {issuedCodeAvailable && (
                      <Button
                        color="success"
                        size="sm"
                        type="button"
                        disabled={claimingCode || !formik.values.unit_id}
                        onClick={() => void handleClaimIssuedCode()}
                      >
                        {claimingCode ? (
                          <Spinner size="sm" />
                        ) : (
                          t("studio.integrations.googleReserve.claimIssuedCta")
                        )}
                      </Button>
                    )}
                  </div>
                  <p className="text-muted small mb-0 mt-2">
                    {t("studio.integrations.googleReserve.pasteFromEmailHint")}
                  </p>
                </div>
              )}
              {credentialFields.map((field) => (
                <div className="mb-3" key={field}>
                  <Label for={`integration-credential-${field}`}>
                    {fieldLabel(field)}
                  </Label>
                  {isGoogleReserve &&
                    isEditMode &&
                    field === "merchant_id" &&
                    maskedSavedCode &&
                    !(formik.values.credentials.merchant_id ?? "").trim() && (
                    <div className="border rounded px-3 py-2 mb-2 bg-light d-flex align-items-center justify-content-between gap-2">
                      <span className="font-monospace small">
                        {t("studio.integrations.googleReserve.merchantLabel", {
                          id: maskedSavedCode,
                        })}
                      </span>
                      <Badge color="secondary" pill>
                        {t("studio.integrations.googleReserve.maskedBadge")}
                      </Badge>
                    </div>
                  )}
                  <Input
                    id={`integration-credential-${field}`}
                    name={`credentials.${field}`}
                    type={
                      field === "channel_key" ||
                      field === "api_url" ||
                      field === "merchant_id"
                        ? "text"
                        : "password"
                    }
                    autoComplete="off"
                    placeholder={
                      isGoogleReserve &&
                      isEditMode &&
                      field === "merchant_id" &&
                      maskedSavedCode
                        ? t("studio.integrations.googleReserve.maskedReplacePlaceholder")
                        : fieldPlaceholder(field)
                    }
                    value={formik.values.credentials[field] ?? ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    onPaste={
                      isGoogleReserve && field === "merchant_id"
                        ? handleMerchantPaste
                        : undefined
                    }
                    disabled={isGoogleReserve && !isEditMode && wizardStep === 3}
                    invalid={
                      formik.touched.credentials?.[field] &&
                      Boolean(
                        (formik.errors.credentials as Record<string, string>)?.[
                          field
                        ]
                      )
                    }
                  />
                  {isGoogleReserve &&
                    field === "merchant_id" &&
                    issuedCodeAvailable &&
                    !(formik.values.credentials.merchant_id ?? "").trim() && (
                    <div className="mt-2">
                      <Button
                        color="soft-success"
                        size="sm"
                        type="button"
                        disabled={claimingCode}
                        onClick={() => void handleClaimIssuedCode()}
                      >
                        {claimingCode ? (
                          <Spinner size="sm" />
                        ) : (
                          t("studio.integrations.googleReserve.claimIssuedCta")
                        )}
                      </Button>
                    </div>
                  )}
                  {credentialsConfigured && isEditMode && field !== "channel_key" && field !== "api_url" && field !== "merchant_id" && (
                    <small className="text-success d-block mt-1">
                      {configuredHint(field)}
                    </small>
                  )}
                  {formik.touched.credentials?.[field] &&
                    (formik.errors.credentials as Record<string, string>)?.[
                      field
                    ] && (
                      <FormFeedback>
                        {
                          (formik.errors.credentials as Record<string, string>)[
                            field
                          ]
                        }
                      </FormFeedback>
                    )}
                  {field === "merchant_id" && (
                    <div className="form-text">
                      {t("studio.integrations.googleReserve.merchantHelp")}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {isGoogleReserve && !isEditMode && wizardStep === 3 && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="fw-semibold mb-2">
                {t("studio.integrations.googleReserve.mappingTitle")}
              </div>
              <div className="d-flex align-items-center flex-wrap gap-2 small mb-2">
                <span className="badge bg-primary-subtle text-primary">
                  {units.find((u) => String(u.id) === formik.values.unit_id)?.name ??
                    t("studio.integrations.googleReserve.unitUnknown")}
                </span>
                <i className="ri-arrow-right-line" aria-hidden />
                <span className="badge bg-dark-subtle text-dark">
                  {t("studio.integrations.googleReserve.merchantLabel", {
                    id: formik.values.credentials.merchant_id || "—",
                  })}
                </span>
              </div>
              <Badge color="warning" className="me-2">
                {t("studio.integrations.googleReserve.env.sandbox")}
              </Badge>
              <p className="text-muted small mb-0 mt-2">
                {t("studio.integrations.googleReserve.wizardReviewHelp")}
              </p>
            </div>
          )}

          {isGoogleReserve && isEditMode && (
            <GoogleReservePanel
              unitId={
                installedIntegration?.unit_id ??
                (formik.values.unit_id ? Number(formik.values.unit_id) : null)
              }
              enabled={formik.values.enabled}
            />
          )}

          {isMetaCommerce && !isEditMode && wizardStep === 3 && (
            <Alert color="success" className="border-0">
              <p className="mb-1 fw-medium">
                {t("studio.integrations.metaCommerce.wizardReviewTitle")}
              </p>
              <p className="mb-0 small text-muted">
                {t("studio.integrations.metaCommerce.wizardReviewHelp")}
              </p>
            </Alert>
          )}

          {isMetaCommerce && isEditMode && (
            <MetaCommercePanel enabled={formik.values.enabled} />
          )}

          {provider.slug === RECOMMENDED_PROVIDER_SLUG && (
            <>
              <hr className="my-3" />
              <h6 className="mb-2">{t("studio.integrations.iugis.templatesSection")}</h6>
              <p className="text-muted small mb-3">
                {t("studio.integrations.iugis.templatesSectionHelp")}
              </p>
              <div className="mb-3">
                <Label for="iugis-template-created">
                  {t("studio.integrations.iugis.templateCreatedLabel")}
                </Label>
                <Input
                  id="iugis-template-created"
                  name="template_created"
                  type="text"
                  value={formik.values.template_created}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("studio.integrations.iugis.templateCreatedPlaceholder")}
                />
              </div>
              <div className="mb-3">
                <Label for="iugis-template-reminder">
                  {t("studio.integrations.iugis.templateReminderLabel")}
                </Label>
                <Input
                  id="iugis-template-reminder"
                  name="template_reminder"
                  type="text"
                  value={formik.values.template_reminder}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("studio.integrations.iugis.templateReminderPlaceholder")}
                />
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <div>
            {isEditMode && installedIntegration && (
              <DisconnectButton
                provider={provider}
                integration={installedIntegration}
                onDisconnected={() => {
                  onUninstalled();
                  onClose();
                }}
              />
            )}
          </div>
          <div className="d-flex gap-2">
            <Button color="light" type="button" onClick={onClose}>
              {t("studio.integrations.actions.cancel")}
            </Button>
            {(isGoogleReserve || isMetaCommerce) && !isEditMode && wizardStep > 1 && (
              <Button
                color="light"
                type="button"
                onClick={() => setWizardStep((step) => Math.max(1, step - 1))}
              >
                {t(
                  isMetaCommerce
                    ? "studio.integrations.metaCommerce.wizardBack"
                    : "studio.integrations.googleReserve.wizardBack"
                )}
              </Button>
            )}
            {isGoogleReserve && !isEditMode && wizardStep < 3 ? (
              <Button
                color="primary"
                type="button"
                onClick={async () => {
                  if (wizardStep === 1) {
                    await formik.setFieldTouched("unit_id", true);
                    if (!formik.values.unit_id) {
                      return;
                    }
                    setWizardStep(2);
                    return;
                  }
                  await formik.setFieldTouched("credentials.merchant_id", true);
                  if (!formik.values.credentials.merchant_id?.trim()) {
                    return;
                  }
                  setWizardStep(3);
                }}
              >
                {t("studio.integrations.googleReserve.wizardNext")}
              </Button>
            ) : isMetaCommerce && !isEditMode && wizardStep < 3 ? (
              <Button
                color="primary"
                type="button"
                onClick={async () => {
                  if (wizardStep === 1) {
                    setWizardStep(2);
                    return;
                  }
                  await formik.setFieldTouched("credentials.catalog_id", true);
                  await formik.setFieldTouched("credentials.access_token", true);
                  if (
                    !formik.values.credentials.catalog_id?.trim() ||
                    !formik.values.credentials.access_token?.trim()
                  ) {
                    return;
                  }
                  setWizardStep(3);
                }}
              >
                {t("studio.integrations.metaCommerce.wizardNext")}
              </Button>
            ) : (
              <Button color="primary" type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting && (
                  <Spinner size="sm" className="me-2">
                    {t("studio.integrations.loading")}
                  </Spinner>
                )}
                {t("studio.integrations.actions.save")}
              </Button>
            )}
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

interface DisconnectButtonProps {
  provider: IntegrationProvider;
  integration: TenantIntegration;
  onDisconnected: () => void;
}

const DisconnectButton: React.FC<DisconnectButtonProps> = ({
  provider,
  integration,
  onDisconnected,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleDisconnect = async (): Promise<void> => {
    setSubmitting(true);

    try {
      await uninstall({
        provider_slug: integration.provider_slug,
        capability: integration.capability,
        unit_id: integration.unit_id,
      });
      onDisconnected();
    } finally {
      setSubmitting(false);
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Button color="danger" outline type="button" onClick={() => setConfirmOpen(true)}>
        {t("studio.integrations.actions.disconnect")}
      </Button>
      <Modal isOpen={confirmOpen} toggle={() => setConfirmOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmOpen(false)}>
          {t("studio.integrations.modal.disconnectTitle")}
        </ModalHeader>
        <ModalBody>
          {t("studio.integrations.modal.disconnectMessage", {
            name: provider.name,
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setConfirmOpen(false)}>
            {t("studio.integrations.actions.cancel")}
          </Button>
          <Button color="danger" onClick={handleDisconnect} disabled={submitting}>
            {submitting && (
              <Spinner size="sm" className="me-2">
                {t("studio.integrations.loading")}
              </Spinner>
            )}
            {t("studio.integrations.actions.confirmDisconnect")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default IntegrationConfigModal;
